/**
 * Currency Conversion Utility & Composable
 * Provides currency conversion functionality with exchange rates
 */

import { computed, ref, watch } from 'vue';
// Optional Firestore caching (free tier). If db is unavailable, features gracefully degrade.
import { db } from '../firebase';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';

/**
 * Currency Conversion Utility Functions
 */

// Default Kenyan Shilling base currency
export const DEFAULT_CURRENCY = {
  code: 'KES',
  name: 'Kenyan Shilling',
  symbol: 'KSh',
  flag: '🇰🇪',
  rate: 1
};

// Common currencies for East African region
export const COMMON_CURRENCIES = [
  { code: 'KES', name: 'Kenyan Shilling', symbol: 'KSh', flag: '🇰🇪', rate: 1 },
  { code: 'USD', name: 'US Dollar', symbol: '$', flag: '🇺🇸', rate: null },
  { code: 'EUR', name: 'Euro', symbol: '€', flag: '🇪🇺', rate: null },
  { code: 'GBP', name: 'British Pound', symbol: '£', flag: '🇬🇧', rate: null },
  { code: 'UGX', name: 'Ugandan Shilling', symbol: 'USh', flag: '🇺🇬', rate: null },
  { code: 'TZS', name: 'Tanzanian Shilling', symbol: 'TSh', flag: '🇹🇿', rate: null },
  { code: 'RWF', name: 'Rwandan Franc', symbol: 'RF', flag: '🇷🇼', rate: null },
  { code: 'ETB', name: 'Ethiopian Birr', symbol: 'Br', flag: '🇪🇹', rate: null },
  { code: 'ZAR', name: 'South African Rand', symbol: 'R', flag: '🇿🇦', rate: null },
  { code: 'NGN', name: 'Nigerian Naira', symbol: '₦', flag: '🇳🇬', rate: null }
];

/**
 * Format currency value with proper symbol and formatting
 */
export const formatCurrencyValue = (amount, currency = DEFAULT_CURRENCY, options = {}) => {
  const {
    showSymbol = true,
    decimals = 2,
    locale = 'en-KE'
  } = options;

  if (amount === null || amount === undefined) return '-';

  const numericAmount = parseFloat(amount) || 0;

  // Format the number with proper locale
  const formatter = new Intl.NumberFormat(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });

  const formattedAmount = formatter.format(Math.abs(numericAmount));
  const sign = numericAmount < 0 ? '-' : '';

  if (!showSymbol) {
    return `${sign}${formattedAmount}`;
  }

  // Handle different symbol positions based on currency
  switch (currency.code) {
    case 'EUR':
      return `${sign}${formattedAmount}€`;
    case 'GBP':
      return `${sign}£${formattedAmount}`;
    case 'USD':
      return `${sign}$${formattedAmount}`;
    case 'KES':
    case 'UGX':
    case 'TZS':
    default:
      return `${sign}${currency.symbol}${formattedAmount}`;
  }
};

/**
 * Convert amount between currencies
 */
export const convertCurrency = (amount, fromCurrency, toCurrency, exchangeRates = {}) => {
  if (!amount || !fromCurrency || !toCurrency) return 0;

  const numericAmount = parseFloat(amount) || 0;

  // If same currency, no conversion needed
  if (fromCurrency.code === toCurrency.code) {
    return numericAmount;
  }

  // Get rates (defaulting to stored rates or 1)
  const fromRate = exchangeRates[fromCurrency.code.toLowerCase()] || fromCurrency.rate || 1;
  const toRate = exchangeRates[toCurrency.code.toLowerCase()] || toCurrency.rate || 1;

  // Convert: amount / fromRate * toRate
  // If fromCurrency is base currency, fromRate should be 1
  const baseAmount = fromRate === 1 ? numericAmount : numericAmount / fromRate;
  const convertedAmount = toRate === 1 ? baseAmount : baseAmount * toRate;

  return parseFloat(convertedAmount.toFixed(6)); // 6 decimals for precision
};

/**
 * Get currency by code from common currencies list
 */
export const getCurrencyByCode = (code) => {
  return COMMON_CURRENCIES.find(currency =>
    currency.code.toLowerCase() === code.toLowerCase()
  ) || DEFAULT_CURRENCY;
};

/**
 * Composable for Currency Management
 */
export const useCurrency = (options = {}) => {
  const {
  baseCurrency = 'KES',
  autoFetch = true,
  cacheTimeout = 30 * 60 * 1000, // 30 minutes in milliseconds
  // New: org-scoped FX caching in Firestore (optional)
  orgId = null,
  enableFirestoreCache = false
  } = options;

  // Reactive state
  const selectedCurrency = ref(getCurrencyByCode(baseCurrency));
  const exchangeRates = ref({});
  const isLoading = ref(false);
  const error = ref(null);
  const lastUpdated = ref(null);

  // Computed properties
  const availableCurrencies = computed(() => {
    return COMMON_CURRENCIES.map(currency => {
      const rate = exchangeRates.value[currency.code.toLowerCase()] || currency.rate;
      return { ...currency, rate };
    });
  });

  const isRatesStale = computed(() => {
    if (!lastUpdated.value) return true;
    const now = new Date();
    const updated = new Date(lastUpdated.value);
    return (now - updated) > cacheTimeout;
  });

  // --- Firestore FX cache helpers (optional) ---
  const yyyymmdd = (d) => {
    const dt = d instanceof Date ? d : new Date(d);
    const m = String(dt.getMonth() + 1).padStart(2, '0');
    const day = String(dt.getDate()).padStart(2, '0');
    return `${dt.getFullYear()}${m}${day}`;
  };

  const getFxDocRef = (dateISO, base) => {
    if (!orgId) return null;
    const key = yyyymmdd(dateISO || new Date());
    return doc(db, `organizations/${orgId}/fxRates/${key}_${(base || baseCurrency).toUpperCase()}`);
  };

  const loadRatesFromFirestore = async (dateISO, targetBaseCurrency = baseCurrency) => {
    try {
      if (!enableFirestoreCache || !orgId) return null;
      const ref = getFxDocRef(dateISO, targetBaseCurrency);
      if (!ref) return null;
      const snap = await getDoc(ref);
      if (!snap.exists()) return null;
      const data = snap.data();
      if (!data || !data.rates) return null;
      // Ensure base matches
      const cachedBase = (data.baseCurrency || '').toLowerCase();
      if (cachedBase !== String(targetBaseCurrency).toLowerCase()) return null;
      return {
        rates: data.rates,
        timestamp: data.createdAt || new Date().toISOString(),
        baseCurrency: cachedBase
      };
    } catch (_) {
      return null;
    }
  };

  const saveRatesToFirestore = async (dateISO, rates, targetBaseCurrency = baseCurrency) => {
    try {
      if (!enableFirestoreCache || !orgId || !rates) return;
      const ref = getFxDocRef(dateISO, targetBaseCurrency);
      if (!ref) return;
      await setDoc(ref, {
        baseCurrency: String(targetBaseCurrency).toUpperCase(),
        rates,
        createdAt: serverTimestamp()
      }, { merge: true });
    } catch (_) {
      // Non-fatal
    }
  };

  // Methods
  const fetchExchangeRates = async (targetBaseCurrency = baseCurrency) => {
    isLoading.value = true;
    error.value = null;

    try {
      const base = targetBaseCurrency.toLowerCase();

      // Try Firestore cache for today first (if enabled)
      try {
        const cachedFx = await loadRatesFromFirestore(new Date(), base);
        if (cachedFx && cachedFx.rates) {
          exchangeRates.value = cachedFx.rates;
          lastUpdated.value = new Date().toISOString();
          // Also write to localStorage cache for fast startup
          const cacheData = {
            rates: exchangeRates.value,
            timestamp: lastUpdated.value,
            baseCurrency: base
          };
          localStorage.setItem('currency_cache', JSON.stringify(cacheData));
          isLoading.value = false;
          return;
        }
      } catch (_) { /* ignore and fallback to network */ }

      // Try primary API first
      const primaryUrl = `https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/${base}.min.json`;
      const fallbackUrl = `https://latest.currency-api.pages.dev/v1/currencies/${base}.min.json`;

      let response;

      try {
        response = await fetch(primaryUrl);
        if (!response.ok) throw new Error('Primary API failed');
      } catch (primaryError) {
        console.warn('Primary currency API failed, trying fallback:', primaryError);
        response = await fetch(fallbackUrl);
        if (!response.ok) throw new Error('Fallback API also failed');
      }

      const data = await response.json();

      if (data[base]) {
        exchangeRates.value = data[base];
        lastUpdated.value = new Date().toISOString();

        // Save to localStorage for caching
        const cacheData = {
          rates: exchangeRates.value,
          timestamp: lastUpdated.value,
          baseCurrency: base
        };
        localStorage.setItem('currency_cache', JSON.stringify(cacheData));

        // Save to Firestore for org-level caching (today)
        await saveRatesToFirestore(new Date(), exchangeRates.value, base);
      }

      error.value = null;
    } catch (err) {
      console.error('Failed to fetch exchange rates:', err);
      error.value = 'Failed to load current exchange rates';

      // Try to load from cache
      loadFromCache();
    } finally {
      isLoading.value = false;
    }
  };

  const loadFromCache = () => {
    try {
      const cached = localStorage.getItem('currency_cache');
      if (cached) {
        const { rates, timestamp, baseCurrency: cachedBase } = JSON.parse(cached);

        // Only use cache if it's for the same base currency and not too old
        if (cachedBase === baseCurrency.toLowerCase()) {
          const cacheAge = new Date() - new Date(timestamp);
          if (cacheAge < (24 * 60 * 60 * 1000)) { // Less than 24 hours
            exchangeRates.value = rates;
            lastUpdated.value = timestamp;
            console.log('Loaded exchange rates from cache');
          }
        }
      }
    } catch (err) {
      console.warn('Failed to load from cache:', err);
    }
  };

  const selectCurrency = (currency) => {
    selectedCurrency.value = currency;
  };

  const convertAmount = (amount, fromCurrency = null, toCurrency = null) => {
    const from = fromCurrency || getCurrencyByCode(baseCurrency);
    const to = toCurrency || selectedCurrency.value;

    return convertCurrency(amount, from, to, exchangeRates.value);
  };

  const formatAmount = (amount, currency = null, options = {}) => {
    const targetCurrency = currency || selectedCurrency.value;
    return formatCurrencyValue(amount, targetCurrency, options);
  };

  // Historical conversion using org-scoped Firestore FX cache when available
  const convertAtDate = async (amount, fromCurrency = null, toCurrency = null, dateISO = null) => {
    const base = (fromCurrency || getCurrencyByCode(baseCurrency));
    const target = (toCurrency || selectedCurrency.value);
    if (!enableFirestoreCache || !orgId) {
      // Fallback to current in-memory rates
      return convertCurrency(amount, base, target, exchangeRates.value);
    }
    const baseCode = (baseCurrency || 'KES').toLowerCase();
    const cachedFx = await loadRatesFromFirestore(dateISO || new Date(), baseCode);
    const rates = cachedFx?.rates || exchangeRates.value;
    return convertCurrency(amount, base, target, rates || {});
  };

  const refreshRates = () => {
    return fetchExchangeRates();
  };

  // Auto-fetch rates on initialization
  if (autoFetch) {
    // Load from cache first for immediate data
    loadFromCache();

    // Then fetch fresh data if cache is stale or empty
    if (isRatesStale.value || Object.keys(exchangeRates.value).length === 0) {
      fetchExchangeRates();
    }
  }

  // Watch for currency changes to update rates if needed
  watch(selectedCurrency, (newCurrency) => {
    // If switching to a currency we don't have rates for, fetch rates
    if (autoFetch && !exchangeRates.value[newCurrency.code.toLowerCase()]) {
      fetchExchangeRates();
    }
  });

  return {
    // State
    selectedCurrency,
    exchangeRates,
    isLoading,
    error,
    lastUpdated,

    // Computed
    availableCurrencies,
    isRatesStale,

    // Methods
    selectCurrency,
    convertAmount,
    formatAmount,
    fetchExchangeRates,
    refreshRates,
    loadFromCache,
  convertAtDate,

    // Utilities
    formatCurrencyValue,
    convertCurrency,
    getCurrencyByCode,

    // Constants
    DEFAULT_CURRENCY,
    COMMON_CURRENCIES
  };
};

// Export default for convenience
export default useCurrency;
